/**
 * Custom Conditions Module for Research Assistant Availability
 *
 * This module defines a registry of custom condition evaluators that can be used
 * to determine if the Research Assistant should be available in a particular context.
 *
 * To add a new custom condition:
 * 1. Add a new condition type to the CustomConditionType union
 * 2. Implement a condition evaluator function
 * 3. Register the evaluator in the conditionRegistry
 */

import { createContext, useContext } from 'react';

/**
 * Type definition for all available custom conditions
 * Extend this union type when adding new condition types
 */
export type CustomConditionType =
  | 'noEventId' // No eventId query param in URL
  | 'hasCohortItems' // Has at least one cohort item
  | 'notAnalyticalDatasetPage' // not on an analytical-dataset surface (RA is unavailable there)
  | 'chatAvailableProjectRoute' // composite: not an analytical-dataset surface
  | string; // For extensibility

/**
 * Interface for condition evaluator context data
 * Contains all the necessary data needed by condition evaluators
 */
export interface ConditionEvaluatorContext {
  url: string; // Current URL
  pathname: string; // URL pathname
  searchParams: URLSearchParams; // URL search parameters
  // Add any additional context data that may be needed by evaluators
}

/**
 * Type definition for a condition evaluator function
 * Takes evaluation context and returns boolean indicating if condition is satisfied
 */
export type ConditionEvaluator = (context: ConditionEvaluatorContext) => boolean;

/**
 * Standalone predicate composed into the registry entries below. Kept as a
 * plain function (not a registry reference) so the composite condition can
 * call it without depending on `conditionRegistry`'s own initialisation
 * order.
 */
// Analytical-dataset surfaces: the unified project-artifact list filtered to
// datasets (`?kind=analytical_dataset`) and the dedicated `/analytical-datasets`
// subtree (list + detail). Neither has a chat surface, so RA stays hidden.
const isAnalyticalDatasetPage = (context: ConditionEvaluatorContext): boolean =>
  context.searchParams.get('kind') === 'analytical_dataset' ||
  /\/analytical-datasets(\/|$)/.test(context.pathname);

/**
 * Registry of all available condition evaluators
 */
export const conditionRegistry: Record<CustomConditionType, ConditionEvaluator> = {
  // Check if eventId or analyticalVariableId is present in the URL
  noEventId: (context) => {
    const eventIdMatch = context.url.match(/eventId=([^&]+)/);
    const analyticalVariableIdMatch = context.url.match(/analyticalVariableId=([^&]+)/);
    return !eventIdMatch && !analyticalVariableIdMatch;
  },

  // Example: Check if cohort has items (placeholder implementation)
  hasCohortItems: () => {
    // This would typically access Redux state or other app state
    // For now, returning true as placeholder
    return true;
  },

  // RA is unavailable on the analytical-dataset surfaces (see
  // `isAnalyticalDatasetPage`).
  notAnalyticalDatasetPage: (context) => !isAnalyticalDatasetPage(context),

  // Composite gate for the gen-3 wide project-tree matcher: chat shows on
  // all project routes except the analytical-dataset surfaces. The cohort
  // builder Results tab is included — the charts are now container-responsive
  // so they reflow rather than overflow when the chat sidebar opens.
  chatAvailableProjectRoute: (context) => !isAnalyticalDatasetPage(context),

  // Default fallback for unknown conditions
  // This allows backward compatibility if a condition is removed
  __unknown__: () => {
    console.warn('Unknown custom condition type encountered');
    return true; // Default to allowing access
  },
};

/**
 * Context for providing custom data to condition evaluators
 */
export const ConditionEvaluatorContextContext = createContext<ConditionEvaluatorContext | null>(
  null
);

/**
 * Hook to evaluate a custom condition based on its identifier
 * @param conditionType - The identifier for the custom condition to evaluate
 * @returns boolean indicating if the condition is satisfied
 */
export const useEvaluateCustomCondition = (conditionType: CustomConditionType): boolean => {
  const context = useContext(ConditionEvaluatorContextContext);

  if (!context) {
    console.warn('ConditionEvaluatorContext not provided');
    return true; // Default to allowing if no context
  }

  // Get the appropriate evaluator from the registry
  const evaluator = conditionRegistry[conditionType] || conditionRegistry.__unknown__;

  // Evaluate the condition
  return evaluator(context);
};

/**
 * Function to evaluate a custom condition outside of React components
 * @param conditionType - The identifier for the custom condition to evaluate
 * @param context - Context data for evaluation
 * @returns boolean indicating if the condition is satisfied
 */
export const evaluateCustomCondition = (
  conditionType: CustomConditionType,
  context: ConditionEvaluatorContext
): boolean => {
  // Get the appropriate evaluator from the registry
  const evaluator = conditionRegistry[conditionType] || conditionRegistry.__unknown__;

  // Evaluate the condition
  return evaluator(context);
};

/**
 * Helper function to get the current context from browser environment
 */
export const getCurrentContext = (): ConditionEvaluatorContext => {
  const url = window.location.href;
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  return { url, pathname, searchParams };
};

/**
 * Interface for route parameters extracted from URL
 */
export interface RouteParams {
  projectId?: string;
  cohortId?: string;
  cohortVersionId?: string;
}

/**
 * Helper function to extract route parameters from URL pathname
 * Supports common route patterns like /projects/:projectId/cohort-builder/:cohortId/:cohortVersionId
 */
export const getRouteParamsFromUrl = (): RouteParams => {
  const pathname = window.location.pathname;

  // Extract projectId from patterns like /projects/{projectId}/...
  const projectIdMatch = pathname.match(/\/projects\/([^/]+)/);
  const projectId = projectIdMatch ? projectIdMatch[1] : undefined;

  // Extract cohortId from patterns like /cohort-builder/{cohortId} or /cohorts/{cohortId}
  const cohortIdMatch = pathname.match(/\/(cohort-builder|cohorts)\/([^/]+)/);
  const cohortId = cohortIdMatch ? cohortIdMatch[2] : undefined;

  // Extract cohortVersionId - typically after cohort-version segment
  // Pattern: /cohort-builder/{cohortId}/cohort-version/{cohortVersionId}
  const cohortVersionIdMatch = pathname.match(
    /\/(cohort-builder|cohorts)\/[^/]+\/cohort-version\/([^/]+)/
  );
  const cohortVersionId = cohortVersionIdMatch ? cohortVersionIdMatch[2] : undefined;

  return {
    projectId,
    cohortId,
    cohortVersionId,
  };
};
